import { DropResult } from "@hello-pangea/dnd";
import { useState, useRef } from "react";

import { useHandleImageUpload } from "0.Mine/1.Hooks/business/images";
import { BusinessService } from "0.Mine/3.Interfaces/Services";

// Define service categories
export const SERVICE_CATEGORIES = [
  "Capelli e Bellezza",
  "Massaggi e Spa",
  "Servizi Domestici",
  "Automotive",
  "Salute e Benessere",
  "Servizi Professionali",
  "Pulizie",
  "Costruzioni",
  "Istruzione e Formazione",
  "Intrattenimento",
  "Eventi",
  "Servizi Finanziari",
  "Fitness",
  "IT e Tecnologia",
  "Servizi Legali",
  "Servizi Medici",
  "Servizi per Animali",
  "Fotografia",
  "Immobiliare",
  "Ristoranti e Alimentari",
  "Vendita al Dettaglio",
  "Trasporti",
  "Viaggi e Turismo",
  "Manifattura",
  "Idraulica",
  "Elettricità",
  "Falegnameria",
  "Marketing",
  "Design",
  "Consulenza",
  "Altro",
];

// New treatment initial state
export const initialNewTreatment = {
  name: "",
  description: "",
  price: "",
  duration: "",
  image: null as File | null, // Changed from string to File | null
  isPopular: false,
  serviceCategory: "Other",
};

export const useTreatmentTable = (
  businessId,
  treatments: BusinessService[],
  onAdd: (treatment: BusinessService) => void,
  onDelete: (index: number) => void,
  onReorder: (treatments: BusinessService[]) => void,
  onUpdate: (treatment: BusinessService) => void // Add this parameter
) => {
  const [isAdding, setIsAdding] = useState(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [newTreatment, setNewTreatment] = useState({ ...initialNewTreatment });
  const [previewUrls, setPreviewUrls] = useState<{ [key: number]: string }>({});
  const [newTreatmentPreview, setNewTreatmentPreview] = useState<string | null>(null);

  // Add state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState<BusinessService | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImageService } = useHandleImageUpload();

  const toggleRow = (index: number): void => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleSubmit = async (formDataOrEvent: React.FormEvent | any): Promise<void> => {
    try {
      // Handle cases where an event object is passed
      if (formDataOrEvent && typeof formDataOrEvent.preventDefault === "function") {
        formDataOrEvent.preventDefault();
      }

      // Determine if we're dealing with form data from the modal or another source
      const formData = formDataOrEvent.target
        ? // Extract data from form event
          {
            name: formDataOrEvent.target.elements?.name?.value || newTreatment.name,
            description:
              formDataOrEvent.target.elements?.description?.value || newTreatment.description,
            price: formDataOrEvent.target.elements?.price?.value || newTreatment.price,
            duration: formDataOrEvent.target.elements?.duration?.value || newTreatment.duration,
            image: newTreatment.image,
            isPopular:
              formDataOrEvent.target.elements?.isPopular?.checked || newTreatment.isPopular,
            serviceCategory:
              formDataOrEvent.target.elements?.category?.value || newTreatment.serviceCategory,
          }
        : // It's already form data from the DataCaptureModal or direct submission
          {
            ...newTreatment,
            ...formDataOrEvent, // Override with any provided values
          };

      // Upload image if available
      let photoUrl = null;
      if (formData.image) {
        try {
          photoUrl = await uploadImageService(formData.image, businessId, "new-service");
        } catch (error) {
          console.error("Failed to upload image:", error);
          // Continue without image if upload fails
        }
      }

      // Create the treatment object with proper type conversion
      const treatment: BusinessService = {
        _id: 1, // temporary id, will be replaced by backend
        name: formData.name,
        description: formData.description,
        price: Number(formData.price) || 0, // Default to 0 if conversion fails
        duration: Number(formData.duration) || 0, // Default to 0 if conversion fails
        discountedPrice: 0,
        photoUrl: photoUrl || formData.image, // Use the uploaded URL if available
        isPopular: Boolean(formData.isPopular),
        serviceCategory: formData.serviceCategory || "Other", // Default to 'Other' if missing
      };

      // Add the new treatment
      onAdd(treatment);
      setNewTreatment({ ...initialNewTreatment });
      setNewTreatmentPreview(null);
      setIsAdding(false);
    } catch (error) {
      console.error("Error adding treatment:", error);
    }
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement | null>,
    isNewTreatment: boolean,
    index?: number
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Generate a preview URL for display
    const previewUrl = URL.createObjectURL(file);

    if (isNewTreatment) {
      // For new treatment, store the file and preview
      setNewTreatment((prev) => ({ ...prev, image: file }));
      setNewTreatmentPreview(previewUrl);
    } else if (typeof index === "number") {
      // For existing treatment, update the file in the treatments array
      const updatedTreatments = [...treatments];
      updatedTreatments[index] = {
        ...updatedTreatments[index],
        photoUrl: file,
      };

      // Store the preview URL in our local state
      setPreviewUrls((prev) => ({
        ...prev,
        [index]: previewUrl,
      }));

      onReorder(updatedTreatments);
    }
  };

  const triggerFileInput = async (treatmentId: number, File: File) => {
    console.log("TREATMENT IMAGE UPLOAD: ", File, "NUMBER:", treatmentId);
    await uploadImageService(File, treatmentId.toString(), businessId.toString());
  };

  const handleDragEnd = (result: DropResult): void => {
    if (!result.destination) return;

    const items = Array.from(treatments);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    onReorder(items);
  };

  const handleNewTreatmentChange = (field: string, value: string | boolean | File | null) => {
    setNewTreatment((prev) => ({ ...prev, [field]: value }));
  };

  const cancelAdding = () => {
    setIsAdding(false);
    setNewTreatment({ ...initialNewTreatment });
    setNewTreatmentPreview(null);
  };

  // Helper function to get the preview URL for a treatment
  const getTreatmentImageUrl = (treatment: BusinessService, index: number): string => {
    // If we have a local preview URL for this index, use it
    if (previewUrls[index]) {
      return previewUrls[index];
    }
    if (treatment.photoUrl instanceof File) {
      return URL.createObjectURL(treatment.photoUrl);
    }

    return typeof treatment.photoUrl === "string" ? treatment.photoUrl : "";
  };

  // Helper function to get the new treatment preview URL
  const getNewTreatmentImageUrl = (): string => {
    return newTreatmentPreview || "";
  };

  // New function to handle editing changes
  const handleEditTreatmentChange = (field: string, value: string | boolean | File | null) => {
    if (editingTreatment) {
      setEditingTreatment((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // New function to handle edit submit
  const handleEditSubmit = async (formDataOrEvent: React.FormEvent | any): Promise<void> => {
    try {
      // Handle cases where an event object is passed
      if (formDataOrEvent && typeof formDataOrEvent.preventDefault === "function") {
        formDataOrEvent.preventDefault();
      }

      if (!editingTreatment) return;

      // Determine if we're dealing with form data from the modal or another source
      const formData = formDataOrEvent.target
        ? // Extract data from form event
          {
            id: editingTreatment._id,
            name: formDataOrEvent.target.elements?.name?.value || editingTreatment.name,
            description:
              formDataOrEvent.target.elements?.description?.value || editingTreatment.description,
            price: formDataOrEvent.target.elements?.price?.value || editingTreatment.price,
            duration: formDataOrEvent.target.elements?.duration?.value || editingTreatment.duration,
            isPopular:
              formDataOrEvent.target.elements?.isPopular?.checked || editingTreatment.isPopular,
            serviceCategory:
              formDataOrEvent.target.elements?.category?.value || editingTreatment.serviceCategory,
            photoUrl: editingTreatment.photoUrl,
          }
        : // It's already form data from the DataCaptureModal
          {
            ...editingTreatment,
            ...formDataOrEvent, // Override with any provided values
          };

      // Handle image if it was changed during editing
      let photoUrl = editingTreatment.photoUrl;
      if (formData.image instanceof File) {
        try {
          photoUrl = await uploadImageService(
            formData.image,
            businessId,
            editingTreatment._id.toString()
          );
        } catch (error) {
          console.error("Failed to upload image:", error);
          // Continue with existing image if upload fails
        }
      }

      // Create the updated treatment object with proper type conversion
      const updatedTreatment: BusinessService = {
        _id: editingTreatment._id,
        name: formData.name,
        description: formData.description,
        price: Number(formData.price) || 0,
        duration: Number(formData.duration) || 0,
        discountedPrice: editingTreatment.discountedPrice || 0,
        photoUrl: photoUrl,
        isPopular: Boolean(formData.isPopular),
        serviceCategory: formData.serviceCategory || "Other",
      };

      // Call the update function
      onUpdate(updatedTreatment);

      // Reset editing state
      setIsEditing(false);
      setEditingTreatment(null);
    } catch (error) {
      console.error("Error updating treatment:", error);
    }
  };

  return {
    isAdding,
    setIsAdding,
    expandedRows,
    newTreatment,
    fileInputRef,
    toggleRow,
    handleSubmit,
    handleImageUpload,
    triggerFileInput,
    handleDragEnd,
    handleNewTreatmentChange,
    cancelAdding,
    getTreatmentImageUrl,
    getNewTreatmentImageUrl,
    isEditing,
    setIsEditing,
    editingTreatment,
    setEditingTreatment,
    handleEditTreatmentChange,
    handleEditSubmit,
  };
};
